import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getSystemStatus } from '../../../services/systemStatusService';
import { checkMaintenanceModeStatus } from '../../../services/errorMonitoringService';

const StatusContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  padding-top: 200px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  
  @media (max-width: 768px) {
    padding-top: 160px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin: 0;
`;

const OverallStatus = styled.div`
  background: ${props => {
    if (props.status === 'operational') return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    if (props.status === 'degraded') return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
    return 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)';
  }};
  color: white;
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const OverallStatusTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const OverallStatusText = styled.p`
  font-size: 1rem;
  margin: 0;
  opacity: 0.95;
`;

const SystemsGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

const SystemCard = styled.div`
  background: white;
  border: 2px solid ${props => {
    if (props.status === 'operational') return '#10b981';
    if (props.status === 'degraded') return '#f59e0b';
    if (props.status === 'outage') return '#ef4444';
    return '#e5e7eb';
  }};
  border-radius: 8px;
  padding: 1.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const SystemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const SystemName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SystemIcon = styled.span`
  font-size: 1.5rem;
`;

const StatusBadge = styled.span`
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    if (props.status === 'operational') return '#d1fae5';
    if (props.status === 'degraded') return '#fef3c7';
    if (props.status === 'outage') return '#fee2e2';
    return '#f3f4f6';
  }};
  color: ${props => {
    if (props.status === 'operational') return '#047857';
    if (props.status === 'degraded') return '#b45309';
    if (props.status === 'outage') return '#b91c1c';
    return '#6b7280';
  }};
`;

const StatusIndicator = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => {
    if (props.status === 'operational') return '#10b981';
    if (props.status === 'degraded') return '#f59e0b';
    if (props.status === 'outage') return '#ef4444';
    return '#9ca3af';
  }};
  box-shadow: 0 0 0 3px ${props => {
    if (props.status === 'operational') return 'rgba(16, 185, 129, 0.2)';
    if (props.status === 'degraded') return 'rgba(245, 158, 11, 0.2)';
    if (props.status === 'outage') return 'rgba(239, 68, 68, 0.2)';
    return 'rgba(156, 163, 175, 0.2)';
  }};
  animation: ${props => props.status === 'operational' ? 'pulse 2s ease-in-out infinite' : 'none'};
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const SystemDescription = styled.p`
  color: #6b7280;
  font-size: 0.95rem;
  margin: 0.5rem 0 0 0;
  line-height: 1.5;
`;

const IncidentAccordion = styled.div`
  margin-top: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: white;
`;

const IncidentHeader = styled.button`
  width: 100%;
  background: #f9fafb;
  border: none;
  padding: 1rem;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
  }
  
  &:focus {
    outline: 2px solid #6366f1;
    outline-offset: -2px;
  }
`;

const IncidentHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`;

const IncidentTitle = styled.div`
  font-weight: 600;
  color: #6b7280;
  font-size: 1rem;
`;

const ChevronIcon = styled.span`
  font-size: 1.2rem;
  color: #6b7280;
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const IncidentContent = styled.div`
  max-height: ${props => props.isOpen ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: #f9fafb;
`;

const IncidentBox = styled.div`
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const IncidentDescription = styled.p`
  color: #4b5563;
  font-size: 0.9rem;
  margin: 0 0 0.5rem 0;
  line-height: 1.6;
`;

const IncidentTimestamp = styled.div`
  color: #6b7280;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  font-style: italic;
`;

const IncidentUpdate = styled.div`
  background: #f9fafb;
  border-left: 4px solid #d1d5db;
  padding: 1rem;
  margin-top: 0.5rem;
  border-radius: 4px;
`;

const IncidentUpdateTitle = styled.div`
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const IncidentUpdateDescription = styled.p`
  color: #4b5563;
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.6;
`;

const IncidentUpdateTimestamp = styled.div`
  color: #6b7280;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  font-style: italic;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 0.9rem;
`;

const RefreshButton = styled.button`
  background: #6366f1;
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1rem;
  
  &:hover {
    background: #4f46e5;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(99, 102, 241, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LastUpdated = styled.div`
  color: #9ca3af;
  font-size: 0.85rem;
  margin-top: 0.5rem;
`;

const SystemStatus = () => {
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [expandedIncidents, setExpandedIncidents] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState({ enabled: false, reason: '' });
  const [systems, setSystems] = useState([
    {
      id: 'website',
      name: 'Website',
      icon: '🌐',
      status: 'operational',
      description: 'Main website and public pages are fully operational.'
    },
    {
      id: 'booking',
      name: 'Booking System',
      icon: '🎫',
      status: 'operational',
      description: 'Online ticket booking and event reservations are working normally.'
    },
    {
      id: 'datacentre',
      name: 'Data Centre',
      icon: '🔒',
      status: 'operational',
      description: 'All servers and infrastructure are running smoothly.'
    },
    {
      id: 'membership',
      name: 'Membership System',
      icon: '👥',
      status: 'operational',
      description: 'Membership renewals and upgrades are working normally.'
    }
  ]);

  // Fetch system status on mount and set up auto-refresh
  useEffect(() => {
    loadSystemStatus();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(loadSystemStatus, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const loadSystemStatus = async () => {
    try {
      setIsLoading(true);
      
      // Load both system status and maintenance mode status
      const [statusData, maintenanceStatus] = await Promise.all([
        getSystemStatus(),
        checkMaintenanceModeStatus()
      ]);
      
      setMaintenanceMode(maintenanceStatus);
      
      if (statusData && statusData.systems) {
        // If maintenance mode is active, ensure booking system shows it
        const updatedSystems = statusData.systems.map(system => {
          if (system.id === 'booking' && maintenanceStatus.enabled) {
            // Override booking system status if maintenance mode is active
            return {
              ...system,
              status: 'outage',
              statusText: 'MAINTENANCE MODE',
              description: maintenanceStatus.reason 
                ? `Booking system is in maintenance mode: ${maintenanceStatus.reason}`
                : 'Booking system is in maintenance mode. New bookings are temporarily disabled.',
              maintenanceMode: {
                enabled: true,
                reason: maintenanceStatus.reason,
                timestamp: maintenanceStatus.timestamp
              }
            };
          }
          return system;
        });
        
        setSystems(updatedSystems);
        if (statusData.lastUpdated) {
          setLastUpdated(new Date(statusData.lastUpdated));
        }
      }
    } catch (error) {
      console.error('[SystemStatus] Failed to load status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    loadSystemStatus();
  };

  const toggleIncident = (systemId) => {
    setExpandedIncidents(prev => ({
      ...prev,
      [systemId]: !prev[systemId]
    }));
  };

  const getOverallStatus = () => {
    const hasOutage = systems.some(s => s.status === 'outage');
    const hasDegraded = systems.some(s => s.status === 'degraded');
    
    if (hasOutage) return { status: 'outage', text: 'Some Systems Experiencing Issues' };
    if (hasDegraded) return { status: 'degraded', text: 'Partial System Outage' };
    return { status: 'operational', text: 'All Systems Operational' };
  };

  const overall = getOverallStatus();

  return (
    <StatusContainer>
      <Header>
        <Title>System Status</Title>
        <Subtitle>Current operational status of Belvoir Castle services</Subtitle>
      </Header>

      <OverallStatus status={overall.status}>
        <OverallStatusTitle>{overall.text}</OverallStatusTitle>
        <OverallStatusText>
          {overall.status === 'operational' && 'All systems are running smoothly.'}
          {overall.status === 'degraded' && 'Some services are experiencing degraded performance.'}
          {overall.status === 'outage' && 'We are experiencing service disruptions and working to resolve them.'}
        </OverallStatusText>
      </OverallStatus>

      <SystemsGrid>
        {systems.map(system => (
          <SystemCard key={system.id} status={system.status}>
            <SystemHeader>
              <SystemName>
                <StatusIndicator status={system.status} />
                <SystemIcon>{system.icon}</SystemIcon>
                {system.name}
              </SystemName>
              <StatusBadge status={system.status}>
                {system.statusText || (system.status === 'operational' && 'Operational') || (system.status === 'degraded' && 'Degraded') || (system.status === 'outage' && 'Outage')}
              </StatusBadge>
            </SystemHeader>
            <SystemDescription>{system.description}</SystemDescription>
            
            {system.maintenanceMode && system.maintenanceMode.enabled && (
              <div style={{
                background: '#fff3cd',
                border: '2px solid #ffc107',
                borderRadius: '8px',
                padding: '1rem',
                marginTop: '1rem'
              }}>
                <div style={{ fontWeight: 600, color: '#856404', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.2rem' }}>⚙️</span>
                  Maintenance Mode Active
                </div>
                {system.maintenanceMode.reason && (
                  <div style={{ color: '#856404', fontSize: '0.9rem', lineHeight: '1.6' }}>
                    <strong>Reason:</strong> {system.maintenanceMode.reason}
                  </div>
                )}
                {system.maintenanceMode.timestamp && (
                  <div style={{ color: '#856404', fontSize: '0.85rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                    Enabled: {new Date(system.maintenanceMode.timestamp).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                )}
              </div>
            )}
            
            {system.incident && (
              <IncidentAccordion>
                <IncidentHeader onClick={() => toggleIncident(system.id)}>
                  <IncidentHeaderContent>
                    <IncidentTitle>
                      {(system.status === 'outage' || system.status === 'degraded') ? '⚠️' : '✓'} {system.incident.title} {(system.status === 'outage' || system.status === 'degraded') ? '(Investigating)' : '(Resolved)'}
                    </IncidentTitle>
                  </IncidentHeaderContent>
                  <ChevronIcon isOpen={expandedIncidents[system.id]}>
                    ▼
                  </ChevronIcon>
                </IncidentHeader>
                <IncidentContent isOpen={expandedIncidents[system.id]}>
                  <IncidentBox>
                    <IncidentDescription>
                      {system.incident.description}
                    </IncidentDescription>
                    <IncidentTimestamp>
                      Detected: {system.incident.timestamp}
                    </IncidentTimestamp>
                  </IncidentBox>
                  {system.incident.updates && system.incident.updates.map((update, index) => (
                    <IncidentUpdate key={index}>
                      <IncidentUpdateTitle>
                        📋 Update
                      </IncidentUpdateTitle>
                      <IncidentUpdateDescription>
                        {update.description}
                      </IncidentUpdateDescription>
                      <IncidentUpdateTimestamp>
                        {update.timestamp}
                      </IncidentUpdateTimestamp>
                    </IncidentUpdate>
                  ))}
                </IncidentContent>
              </IncidentAccordion>
            )}
          </SystemCard>
        ))}
      </SystemsGrid>

      <Footer>
        <RefreshButton onClick={handleRefresh}>
          🔄 Refresh Status
        </RefreshButton>
        <LastUpdated>
          Last updated: {lastUpdated.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}
        </LastUpdated>
        <p style={{ marginTop: '1rem' }}>
          If you're experiencing issues not listed here, please contact{' '}
          <a href="mailto:techsupport@belvoircastle.com" style={{ color: '#6366f1', textDecoration: 'none' }}>
            techsupport@belvoircastle.com
          </a>
        </p>
      </Footer>
    </StatusContainer>
  );
};

export default SystemStatus;

